import { useApolloClient } from "@apollo/client";
import { getNameWithCopy } from "@msys/common";
import {
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  renderMenuWithTheme,
} from "@msys/ui";
import {
  ContentCopy as ContentCopyIcon,
  DeleteOutlined as DeleteOutlineIcon,
  EditOutlined as EditOutlineIcon,
} from "@mui/icons-material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { EditProductListModal } from "./EditProductListModal.js";
import {
  useDeleteProductListMutation,
  useDuplicateProductListMutation,
} from "../../main-routes/products/ProductLists.generated.js";

/**
 * Per-list actions menu (rename / duplicate / delete), gated by `pim_catalogue_update`.
 * Self-contained (owns its mutations) so it can be reused wherever a product
 * list is listed.
 */
export const ProductListActionsMenu = ({
  productList,
}: {
  productList: { id: string; title: string };
}) => {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const client = useApolloClient();

  const [duplicateProductList] = useDuplicateProductListMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      namedOperations.Query.ProductLists,
      namedOperations.Query.ProductListsAuthors,
    ],
  });

  const [deleteProductList] = useDeleteProductListMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      namedOperations.Query.ProductLists,
      namedOperations.Query.ProductListsAuthors,
    ],
  });

  return (
    <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
      <MenuButton
        renderMenu={renderMenuWithTheme}
        buttonProps={{ color: "inherit" }}
      >
        <ModalOpenButton
          Modal={EditProductListModal}
          modalProps={{
            productListId: productList.id,
            title: productList.title,
          }}
        >
          <MenuItemWithIcon icon={<EditOutlineIcon />}>
            {t("Rename", { ns: "Global" })}
          </MenuItemWithIcon>
        </ModalOpenButton>
        <ModalOpenButton
          Modal={ConfirmModal}
          modalProps={{
            title: t("Are you sure you want to duplicate this product list?", {
              ns: "ProductLists",
            }),
            handleConfirm: async () => {
              await duplicateProductList({
                variables: {
                  input: {
                    productListId: productList.id,
                    title: getNameWithCopy(
                      productList.title,
                      t("copy", { ns: "Global" })
                    ),
                  },
                },
              });
            },
          }}
        >
          <MenuItemWithIcon icon={<ContentCopyIcon />}>
            {t("Duplicate", { ns: "ProductLists" })}
          </MenuItemWithIcon>
        </ModalOpenButton>
        <ModalOpenButton
          Modal={ConfirmModal}
          modalProps={{
            title: t("Are you sure you want to remove this product list?", {
              ns: "ProductLists",
            }),
            handleConfirm: async () => {
              await deleteProductList({
                variables: { input: { productListId: productList.id } },
              });
            },
          }}
        >
          <MenuItemWithIcon icon={<DeleteOutlineIcon />}>
            {t("Delete", { ns: "Global" })}
          </MenuItemWithIcon>
        </ModalOpenButton>
      </MenuButton>
    </RestrictedByOrganisationPermissionWithDebug>
  );
};
